import React, { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { saveReturnTo } from './utils/postLoginDestination';
import FloatingLanguageSwitcher from './components/FloatingLanguageSwitcher';
import { VerificationProvider } from './context/VerificationContext';
import VerificationModeToggle from './components/VerificationModeToggle';

// Eager — DOAR homepage (LCP critic, 90%+ vizitatori land aici)
import LandingPage from './pages/LandingPage';

// Lazy — toate celelalte pagini, inclusiv auth (split in chunks separate, incarcate la demand).
// Auth pages erau eager dar adaugau ~50-80KB main bundle inutil pentru first-time visitors
// (90% userilor vin pe /, doar 10% direct pe /login). 2026-05-23 — mutate la lazy.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ActivatePage = lazy(() => import('./pages/ActivatePage'));

const BankingLandingPage = lazy(() => import('./pages/BankingLandingPage'));
const FarmaciiLandingPage = lazy(() => import('./pages/FarmaciiLandingPage'));
const MetodologiePage = lazy(() => import('./pages/MetodologiePage'));
const BranduriIndexPage = lazy(() => import('./pages/BranduriIndexPage'));
const BrandPublicPage = lazy(() => import('./pages/BrandPublicPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CookiesPage = lazy(() => import('./pages/CookiesPage'));
const SubProcesatoriPage = lazy(() => import('./pages/SubProcesatoriPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'));
// Rezultatul unui mini-scan, redeschis dintr-un link partajat. noindex (vezi pagina).
const ScanResultPage = lazy(() => import('./pages/ScanResultPage'));
const AnnualReportPage = lazy(() => import('./pages/AnnualReportPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const RriLeaderboardPage = lazy(() => import('./pages/RriLeaderboardPage'));
const VersusPage = lazy(() => import('./pages/VersusPage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const GlossaryIndexPage = lazy(() => import('./pages/GlossaryIndexPage'));
const GlossaryTermPage = lazy(() => import('./pages/GlossaryTermPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const BrandDetail = lazy(() => import('./pages/BrandDetail'));
const DashboardAnalitic = lazy(() => import('./pages/DashboardAnalitic'));
const ExecutiveSummary = lazy(() => import('./pages/ExecutiveSummary'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AlertsMonitorPage = lazy(() => import('./pages/AlertsMonitorPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const BillingSuccessPage = lazy(() => import('./pages/BillingSuccessPage'));
const BillingCancelPage = lazy(() => import('./pages/BillingCancelPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));

import CookieBanner from './components/CookieBanner';
import HelpWidget from './components/HelpWidget';
import LazyLoadErrorBoundary from './components/LazyLoadErrorBoundary';
import { Toaster } from 'react-hot-toast';
import './styles/globals.css';

function PrivateRoute({ children, allowDuringOnboarding = false }) {
  const { t } = useTranslation();
  const { user, loading, isOnboarded } = useAuth();
  if (loading) return <div className="loading-screen">{t('app.common.loading')}</div>;
  if (!user) {
    // 2026-08-19 — inregistram unde voia userul sa ajunga. Pana acum doar
    // interceptorul de 401 din utils/api.js scria returnTo, deci un user
    // delogat care deschidea un link /brand/:id ajungea pe dashboard, nu
    // acolo: poarta SPA si poarta XHR foloseau mecanisme diferite si doar a
    // doua pastra intentia.
    try {
      saveReturnTo(window.location.pathname + window.location.search + window.location.hash);
    } catch {}
    return <Navigate to="/login" />;
  }
  if (!allowDuringOnboarding && !isOnboarded()) return <Navigate to="/onboarding" />;
  return children;
}

function AdminRoute({ children }) {
  const { t } = useTranslation();
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div className="loading-screen">{t('app.common.loading')}</div>;
  if (!user) return <Navigate to="/login" />;
  if (!isAdmin()) return <Navigate to="/dashboard" />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  // 2026-08-19 — asteptam hidratarea inainte de a decide. Fara `loading`, la
  // primul render `user` e null chiar si pentru un user logat (useAuth il pune
  // dupa citirea din localStorage), deci se randa LoginPage, se descarca
  // chunk-ul lui si abia apoi urma redirectul: un flash de PageFallback si un
  // chunk degeaba pentru oricine deschide /login din bookmark sau cu Back.
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : children;
}

/** Help widget — renderez doar pentru useri logati (nu pe paginile publice de marketing). */
function ConditionalHelpWidget() {
  const { user } = useAuth();
  if (!user) return null;
  return <HelpWidget />;
}

function PageFallback() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t('app.common.loading_page_aria')}
      style={{
        minHeight: '70vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'DM Mono, monospace',
        color: '#D4AF37',
        letterSpacing: 4,
        fontSize: 12,
        textTransform: 'uppercase',
      }}
    >
      {t('app.common.loading_caps')}
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <VerificationProvider>
        <BrowserRouter>
          <div className="app-shell">
            {/* 2026-05-15 — Floating switcher pe paginile publice fără Navbar/header propriu */}
            <FloatingLanguageSwitcher />
            {/* 2026-05-18 — LazyLoadErrorBoundary wrap Suspense pentru a prinde
                chunk load failures (ex: deploy window când main.js referează
                chunk-uri ne-deployate încă → Vercel returnează HTML → parse error).
                Auto-reload după 2.5s, max 2 retries, apoi manual refresh UI. */}
            <LazyLoadErrorBoundary>
              <Suspense fallback={<PageFallback />}>
                <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/banking" element={<BankingLandingPage />} />
                <Route path="/farmacii" element={<FarmaciiLandingPage />} />
                <Route path="/metodologie" element={<MetodologiePage />} />
                <Route path="/preturi" element={<PricingPage />} />
                <Route path="/politica-confidentialitate" element={<PrivacyPolicyPage />} />
                <Route path="/termeni-si-conditii" element={<TermsPage />} />
                <Route path="/cookies" element={<CookiesPage />} />
                <Route path="/sub-procesatori" element={<SubProcesatoriPage />} />
                <Route path="/retur" element={<RefundPolicyPage />} />
                <Route path="/branduri" element={<BranduriIndexPage />} />
                <Route path="/branduri/:industry" element={<BranduriIndexPage />} />
                <Route path="/branduri/:industry/:slug" element={<BrandPublicPage />} />
                <Route path="/comparatie/:slug1/:slug2" element={<ComparisonPage />} />
                <Route path="/scan/:scanId" element={<ScanResultPage />} />
                <Route path="/raport-2026" element={<AnnualReportPage />} />
                <Route path="/clasament/:industry" element={<LeaderboardPage />} />
                <Route path="/clasament-reputatie/:industry" element={<RriLeaderboardPage />} />
                <Route path="/vs/:competitor" element={<VersusPage />} />
                <Route path="/insights/:industry" element={<InsightsPage />} />
                <Route path="/blog" element={<BlogIndexPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/glosar" element={<GlossaryIndexPage />} />
                <Route path="/glosar/:slug" element={<GlossaryTermPage />} />
                <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
                <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/activate" element={<ActivatePage />} />
                <Route path="/onboarding" element={<PrivateRoute allowDuringOnboarding><OnboardingPage /></PrivateRoute>} />
                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/brand/:brandId" element={<PrivateRoute><BrandDetail /></PrivateRoute>} />
                <Route path="/dashboard-analitic/:brandId" element={<PrivateRoute><DashboardAnalitic /></PrivateRoute>} />
                <Route path="/sumar-executiv/:brandId" element={<PrivateRoute><ExecutiveSummary /></PrivateRoute>} />
                <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
                <Route path="/admin/alerts" element={<AdminRoute><AlertsMonitorPage /></AdminRoute>} />
                <Route path="/account" element={<PrivateRoute><AccountPage /></PrivateRoute>} />
                <Route path="/billing/success" element={<PrivateRoute><BillingSuccessPage /></PrivateRoute>} />
                <Route path="/billing/cancel" element={<PrivateRoute><BillingCancelPage /></PrivateRoute>} />
                <Route path="/dezabonare" element={<UnsubscribePage />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
              </Suspense>
            </LazyLoadErrorBoundary>
            <CookieBanner />
            <ConditionalHelpWidget />
            <VerificationModeToggle />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: 15,
                  background: '#ffffff',
                  color: '#0d1b2a',
                  border: '1px solid rgba(201,145,17,0.2)',
                  borderRadius: 12,
                  boxShadow: '0 16px 40px rgba(15,23,42,0.12)',
                  padding: '14px 18px',
                },
                success: { iconTheme: { primary: '#2d6a4f', secondary: '#fff' } },
                error: { iconTheme: { primary: '#c0392b', secondary: '#fff' }, duration: 6000 },
              }}
            />
          </div>
        </BrowserRouter>
        </VerificationProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
